/**
 * P2-S4 교사 콘솔 API(C-07 · 세션 역할 teacher · 코스 경계 — 역할은 서버 세션에서만):
 *   GET  /api/courses/{c}/teacher/room/console → { room | null, modes, students, roster }
 *   POST /api/courses/{c}/teacher/room/cmd { kind: pause|resume|reset|stop|resetMachine|isolate|restore|timer, pid?, seconds? }
 *        → 200 { room } · 404 룸 없음 · 422 필드 · 503 FE-303
 *   POST /api/courses/{c}/teacher/room/teams { kind: "teams", teams: [[pid…]…] } | { kind: "pairs", pairs: [[pid, pid]…] }
 *        → 200 · 422(팀 크기 2~4 밖 · 모르는 pid · 한 학생 두 번 · 모드 안 맞음 field "mode") · 503 FE-303
 *   모드 전환 · 룸 닫기는 기존 …/teacher/room/mode · …/teacher/room/close(./api.ts).
 * 점수·순위 필드 없음 — 정렬은 셸이(이름/연결만).
 *
 * P2-S5 마무리(계약 §2.3 · §2.4):
 *   POST …/teacher/room/cmd { kind: "highlight", pid | null } — 칠판 하이라이트(★)
 *   GET  …/teacher/room/script → { templates, state | null, activityTemplate | null }
 *   POST …/teacher/room/script { action: start(template) | next | skip | pause | resume | stop } → { state }
 *        · 404 룸 없음 · 409 스크립트 없음 · 422 · 503 FE-303
 *   GET  …/teacher/room/board → { roomId, wsUrl, joinToken }(role board · 5분 — 교사만)
 *   POST …/teacher/room/layout { objects: SandboxObject[] ≤50 } — 자유 놀이터에서만(아니면 422 mode)
 *
 * P3-S4 교류 룸(계약 §3 — SR-ROOM-05 · FS-11-01 공동 호스트 변형):
 *   console 응답에 kind(class|exchange) · courses:[{courseId,label,host,mine}] · 학생 줄 courseId · mine
 *   · 자기 반이 아닌 학생 대상 명령(resetMachine · isolate · restore · highlight)은 403(Q-5 — 셸은 버튼을 미리 끈다)
 *   · 교류 룸이면 모드 목록에 릴레이가 없다(Q-3 — 셸도 한 번 더 거른다)
 */
import { useMemo } from "react";
import { ApiError } from "../../../lib/http/errors";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import { readScript, readTemplates } from "../lib/script";
import type { RelayStageStatus } from "@picnic-field/modes";
import type { Requester } from "../../../types/api";
import type {
  Connection,
  Activity,
  ConsoleRoom,
  ConsoleStudent,
  ConsoleCourse,
  TeacherConsole,
  ConsoleCmd,
  SandboxObject,
  ScriptAction,
  ScriptInfo,
  TeamsBody,
  RoomTicket,
  ScriptState,
} from "../types";

/** 409 — 진행 중인 스크립트가 없다(next/skip/pause/resume/stop) */
export class NoScript extends Error {
  constructor() {
    super("no script");
    this.name = "NoScript";
  }
}

/** 422 — 서버가 받지 않은 필드(예: "mode" · "teams" · "pid") */
export class CmdRejected extends Error {
  constructor(readonly field: string) {
    super(`rejected: ${field}`);
    this.name = "CmdRejected";
  }
}

export interface ConsoleApi {
  console(): Promise<TeacherConsole>;
  cmd(c: ConsoleCmd): Promise<ConsoleRoom | null>;
  teams(body: TeamsBody): Promise<void>;
  /** P2-S5 마무리 스크립트 */
  script(): Promise<ScriptInfo>;
  scriptAction(a: ScriptAction): Promise<ScriptState | null>;
  /** P2-S5 칠판 조인 토큰(role board) — 칠판 뷰 · 배치 도구 미리보기가 쓴다 */
  board(): Promise<RoomTicket>;
  /** P2-S5 자유 놀이터 배치 적용 · decor = 교사 꾸밈(2026-10-11 · 보기 전용 — 없으면 꾸밈을 비운다) */
  layout(objects: readonly SandboxObject[], decor?: object): Promise<void>;
}

const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const obj = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

/** P2-S1 가짜·옛 응답은 로비를 "gallery" 로 준다 — 콘솔은 "lobby" 하나로 */
export const normMode = (id: string) => (id === "gallery" || id === "" ? "lobby" : id);

export function readRoom(v: unknown): ConsoleRoom | null {
  const o = obj(v);
  if (!o || typeof o.roomId !== "string") return null;
  const st = str(o.state);
  const timer = obj(o.timer);
  const ends = num(timer?.endsInS);
  return {
    roomId: o.roomId,
    state: st === "prewarm" || st === "paused" ? st : "open",
    modeId: normMode(str(o.modeId) ?? ""),
    presetKey: str(o.presetKey) ?? "",
    capacity: num(o.capacity) ?? 30,
    server: o.server === "up" || o.server === "down" ? o.server : null,
    podId: str(o.podId),
    timer: ends !== null && ends > 0 ? { endsInS: ends } : null,
    // 밀어내기 한 판 시간(초 · 2026-10-11 — 예전에는 여기서 빠져 화면이 늘 1분이었다)
    roundS: typeof o.roundS === "number" && Number.isInteger(o.roundS) ? o.roundS : null,
    openedAt: str(o.openedAt),
    highlight: num(o.highlight),
    boards: num(o.boards) ?? 0,
  };
}

function readActivity(v: unknown): Activity | null {
  const o = obj(v);
  switch (o?.kind) {
    case "match":
      return {
        kind: "match",
        vs: String(o.vs ?? ""),
        ...(o.paused === true ? { paused: true } : {}),
      };
    case "team":
      return { kind: "team", team: num(o.team) ?? 0 };
    case "relay": {
      const st = str(o.status);
      return {
        kind: "relay",
        stage: num(o.stage) ?? 0,
        status: (["waiting", "armed", "running", "done", "failed", "skipped"] as const).includes(
          st as RelayStageStatus,
        )
          ? (st as RelayStageStatus)
          : "waiting",
      };
    }
    case "queue":
    case "spectate":
    case "free":
      return { kind: o.kind };
    default:
      return null;
  }
}

const CONNECTIONS: readonly Connection[] = ["online", "dropped", "offline", "isolated"];

function readStudent(v: unknown): ConsoleStudent | null {
  const o = obj(v);
  if (!o || typeof o.pid !== "string") return null;
  const w = obj(o.work);
  const c = str(o.connection);
  return {
    pid: o.pid,
    courseId: str(o.courseId),
    mine: o.mine !== false,
    nickname: str(o.nickname) ?? "—",
    avatar: str(o.avatar),
    machine: num(o.machine),
    work: w ? { version: num(w.version) ?? 1, title: str(w.title) ?? "" } : null,
    connection: c && (CONNECTIONS as readonly string[]).includes(c) ? (c as Connection) : "offline",
    droppedS: num(o.droppedS),
    stalled: Array.isArray(o.stalled)
      ? o.stalled.filter((x): x is string => typeof x === "string")
      : [],
    activity: readActivity(o.activity),
  };
}

/** 교류 룸 반 구분 색(호스트 먼저 a · b — 콘솔과 칠판이 같은 순서) */
export const courseTone = (
  courses: readonly ConsoleCourse[],
  courseId: string | null,
): "a" | "b" | undefined => {
  const i = courses.findIndex((c) => c.courseId === courseId);
  return i < 0 ? undefined : i === 0 ? "a" : "b";
};

function readCourses(v: unknown): ConsoleCourse[] {
  const xs = (Array.isArray(v) ? v : [])
    .map(obj)
    .filter((c): c is Record<string, unknown> => !!c && typeof c.courseId === "string")
    .map((c) => ({
      courseId: String(c.courseId),
      label: str(c.label) ?? String(c.courseId),
      host: c.host === true,
      mine: c.mine === true,
    }));
  // 호스트 먼저(칠판 반 구분 색 순서가 두 교사 화면에서 같게)
  return xs.sort((a, b) => Number(b.host) - Number(a.host));
}

export function readConsole(v: unknown): TeacherConsole {
  const o = obj(v) ?? {};
  const roster = obj(o.roster);
  const roomObj = obj(o.room);
  const kind = (str(o.kind) ?? str(roomObj?.kind)) === "exchange" ? "exchange" : "class";
  return {
    room: readRoom(o.room),
    kind,
    courses: readCourses(o.courses ?? roomObj?.courses),
    modes: (Array.isArray(o.modes) ? o.modes : [])
      .map(obj)
      .filter((m): m is Record<string, unknown> => !!m && typeof m.id === "string")
      .map((m) => ({
        id: normMode(String(m.id)),
        kind: str(m.kind) ?? "lobby",
        recommended: m.recommended === true,
      }))
      .filter((m) => kind !== "exchange" || (m.kind !== "relay" && m.id !== "relay-machine")),
    students: (Array.isArray(o.students) ? o.students : [])
      .map(readStudent)
      .filter((s): s is ConsoleStudent => !!s),
    roster: roster
      ? {
          source: roster.source === "nrps" ? "nrps" : "local",
          count: num(roster.count) ?? 0,
          syncedAt: str(roster.syncedAt),
        }
      : null,
  };
}

const rejected = (e: unknown): never => {
  if (e instanceof ApiError && e.status === 422) {
    const b = obj(e.body);
    throw new CmdRejected(str(b?.field) ?? str(b?.error) ?? "body");
  }
  throw e;
};

export function createConsoleApi(request: Requester, courseId: string): ConsoleApi {
  const c = `/api/courses/${encodeURIComponent(courseId)}/teacher/room`;
  const post = (body: unknown): RequestInit => ({
    method: "POST",
    body: JSON.stringify(body),
    headers: { "Content-Type": "application/json" },
  });
  return {
    console: async () => readConsole(await request<unknown>(`${c}/console`)),
    async cmd(body) {
      try {
        const r = await request<unknown>(`${c}/cmd`, post(body));
        return readRoom(obj(r)?.room);
      } catch (e) {
        return rejected(e);
      }
    },
    async teams(body) {
      try {
        await request<unknown>(`${c}/teams`, post(body));
      } catch (e) {
        rejected(e);
      }
    },
    async script() {
      const o = obj(await request<unknown>(`${c}/script`)) ?? {};
      const at = o.activityTemplate;
      return {
        templates: readTemplates(o.templates),
        state: readScript(o.state),
        activityTemplate: at === "view" || at === "challenge" || at === "team" ? at : null,
      };
    },
    async scriptAction(a) {
      try {
        return readScript(obj(await request<unknown>(`${c}/script`, post(a)))?.state);
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) throw new NoScript();
        return rejected(e);
      }
    },
    async board() {
      const o = obj(await request<unknown>(`${c}/board`));
      if (!o || typeof o.wsUrl !== "string" || typeof o.joinToken !== "string")
        throw new Error("board ticket");
      return { roomId: str(o.roomId) ?? "", wsUrl: o.wsUrl, token: o.joinToken };
    },
    async layout(objects, decor) {
      try {
        await request<unknown>(`${c}/layout`, post(decor ? { objects, decor } : { objects }));
      } catch (e) {
        rejected(e);
      }
    },
  };
}

export function useConsoleApi(): ConsoleApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const courseId =
    state.status === "ready" && state.session.role === "teacher" ? state.session.courseId : null;
  return useMemo(
    () => (courseId ? createConsoleApi(manager.request, courseId) : null),
    [manager, courseId],
  );
}
